import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { Toaster as Sonner } from "~/components/ui/sonner";
import { Toaster } from "~/components/ui/toaster";

import Analytics from "./components/Analytics";
import { NotificationListener } from "./components/NotificationListener";
import stylesheetHref from "./index.css?url";
import SearchLessonDialog from "./modules/Client/Courses/SearchLesson/SearchLesson";
import CustomErrorBoundary from "./modules/common/ErrorBoundary/ErrorBoundary";
import { Providers } from "./modules/Global/Providers";

import type { LinksFunction } from "@remix-run/node";
import type { MouseEvent } from "react";

export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: stylesheetHref },
    { rel: "icon", href: "/favicon.ico", sizes: "any" },
    { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    { rel: "apple-touch-icon-precomposed", href: "/apple-touch-icon-precomposed.png" },
  ];
};

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isLessonPage = /^\/(trial\/)?course\/[^/]+\/lesson\/[^/]+$/.test(location.pathname);

  // Set after hydration: rendering a <style> only on lesson URLs made the head differ from the
  // prerendered HTML, so React 18 re-rendered the whole document.
  useEffect(() => {
    document.documentElement.toggleAttribute("data-lesson-page", isLessonPage);
  }, [isLessonPage]);

  return (
    <html lang="pl" className="scroll-smooth">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Toaster />
        <Sonner position="top-right" toastOptions={{ duration: 100000 }} />
      </body>
    </html>
  );
}

function SkipToContentLink() {
  const { t } = useTranslation();

  const focusMain = (event: MouseEvent<HTMLAnchorElement>) => {
    const main = document.querySelector("main");
    if (!main) return;

    event.preventDefault();
    main.tabIndex = -1;
    main.focus();
  };

  return (
    <Button
      asChild
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[1000]"
    >
      <a href="#main" onClick={focusMain}>
        {t("common.skipToContent")}
      </a>
    </Button>
  );
}

function Root() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  return (
    <Providers>
      <SkipToContentLink />
      <Analytics />
      <NotificationListener />
      <Outlet context={{ onSearchClick: () => setIsSearchOpen(true) }} />
      <SearchLessonDialog open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </Providers>
  );
}

export default withSentry(Root);

export function HydrateFallback() {
  return <div />;
}
export function ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  if (isRouteErrorResponse(error)) {
    return <CustomErrorBoundary stack={error.data} message={error.statusText} />;
  } else if (error instanceof Error) {
    return <CustomErrorBoundary stack={error.stack} message={error.message} />;
  } else {
    return <CustomErrorBoundary />;
  }
}
